/**
 * Shrink large photos/screenshots before upload. The production server
 * rejects request bodies over ~1MB (HTTP 413), and big images also slow
 * down the POS. GIF/SVG and already-small files are left untouched.
 */
const TARGET_BYTES = 900 * 1024;
const MAX_DIMENSION = 1600;
const COMPRESSIBLE = ["image/png", "image/jpeg", "image/jpg", "image/webp"];

const loadImage = (file: File) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (err) => {
      URL.revokeObjectURL(url);
      reject(err);
    };
    img.src = url;
  });

const toBlob = (canvas: HTMLCanvasElement, type: string, quality: number) =>
  new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, quality));

export const compressImage = async (file: File): Promise<File> => {
  if (!COMPRESSIBLE.includes(file.type) || file.size <= TARGET_BYTES) return file;

  try {
    const img = await loadImage(file);
    let scale = Math.min(1, MAX_DIMENSION / Math.max(img.width, img.height));

    // WebP keeps transparency (logos) and compresses well; try a few
    // quality/size steps until it fits.
    for (let attempt = 0; attempt < 5; attempt += 1) {
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(img.width * scale));
      canvas.height = Math.max(1, Math.round(img.height * scale));
      canvas.getContext("2d")?.drawImage(img, 0, 0, canvas.width, canvas.height);

      const quality = Math.max(0.5, 0.85 - attempt * 0.1);
      const blob = await toBlob(canvas, "image/webp", quality);
      if (blob && blob.size <= TARGET_BYTES) {
        const name = file.name.replace(/\.[^.]+$/, "") + ".webp";
        return new File([blob], name, { type: "image/webp" });
      }
      scale *= 0.8;
    }
  } catch {
    // Fall through and upload the original.
  }
  return file;
};
